import { ActionTitle } from "@/components/actionTitle/ActionTitle";
import { AppointmentFilterDefault } from "@/features/calendar/calendarFilter/appointmentFilterReducer";
import { useCalendarAppointments } from "@/features/calendar/hooks/useCalendarAppointments";
import {
  openCalendarModal,
  openCalendarModalCreator,
} from "@/features/calendar/openCalendarModal";
import {
  type CoWorkerScheduleActivity,
  type OutlookEvent,
} from "@/features/calendar/types";
import { useUserCalendarEvents } from "@/features/calendar/useUserCalendar";
import { isScheduleActivity } from "@/features/calendar/utils";
import { sortCalendarItems } from "@/features/calendar/utils/sortItems";
import { mapScheduleToActivity } from "@/features/calendar/work-schedule/getScheduleActivities";
import { useModals } from "@/features/modals/hooks";
import { ScheduleActivityModal } from "@/features/modals/scheduleActivityModal/ScheduleActivityModal";
import {
  ScrollContainer,
  WidgetContainer,
} from "@/features/workspace/widgets/Components";
import styled from "@emotion/styled";
import { useCalendarSettings } from "@repo/api";
import {
  type Appointment,
  type Task,
  type WidgetSetting,
  type WidgetSize,
  useProfile,
  useSchedules,
} from "@repo/api";
import { Text } from "@repo/skapa";
import { T, t } from "@repo/translation";
import {
  ElementsContainer,
  Line,
  LocalizedDayDate,
  LocalizedTime,
  translateLabel,
  useSettings,
} from "@repo/ui";
import { differenceInMinutes } from "date-fns";
import { Fragment, useState } from "react";

import { CARD_HEIGHT_NORMAL, CARD_HEIGHT_TALL } from "../constants";
import { normalizeItem } from "./normalizeItem";

const ColumnsContainer = styled.div`
  display: grid;
  grid-template-columns: 50px 2fr;
  gap: 15px;

  cursor: pointer;
`;

const MeetingLength = ({
  allDay,
  startDate,
  endDate,
}: {
  allDay: boolean;
  startDate?: Date;
  endDate?: Date;
}) => {
  if (allDay) {
    return null;
  }
  const length =
    startDate && endDate ? differenceInMinutes(endDate, startDate) : 30;

  return <div>{`${length} ${t("label.minutes")}`}</div>;
};

export const CalendarWidget = ({
  title,
  settings,
  size,
}: {
  title: string;
  size: WidgetSize;
  settings?: WidgetSetting;
}) => {
  const profile = useProfile();
  const { pushModal } = useModals();
  const { isLoadingSettings } = useSettings();

  const [scheduledItem, setScheduledItem] = useState<
    CoWorkerScheduleActivity | undefined
  >(undefined);

  const today = new Date();

  const { data: defaultSettings } = useCalendarSettings();
  const filters = {
    ...AppointmentFilterDefault,
    calendarSettings: {
      ...defaultSettings,
      showOutlookCalendar: Boolean(
        settings?.OUTLOOK !== undefined ? settings.OUTLOOK : false
      ),
      showScheduleCalendar: Boolean(
        settings?.SCHEDULE !== undefined ? settings.SCHEDULE : false
      ),
    },
    agendaCalendar: profile.data?.mail
      ? {
          emailId: [profile.data?.mail],
        }
      : AppointmentFilterDefault.agendaCalendar,
  };

  const shouldSearchAppointments = !isLoadingSettings;

  const { items } = useCalendarAppointments(shouldSearchAppointments, filters);
  const { data: outlookEvents } = useUserCalendarEvents(filters);

  const { data: schedules } = useSchedules({
    emailId:
      (filters.calendarSettings.showScheduleCalendar && profile.data?.mail) ||
      undefined,
    fromDate: filters.selectedDate,
    toDate: filters.selectedDate,
  });

  const scheduleActivities = (schedules ?? []).map(mapScheduleToActivity);

  const allItems = [
    ...items,
    ...(outlookEvents ?? []),
    ...scheduleActivities,
  ].sort(sortCalendarItems);

  const openModal = openCalendarModalCreator(items, pushModal);

  const handleOpenModal = (
    item: Task | Appointment | OutlookEvent | CoWorkerScheduleActivity
  ) => {
    if (isScheduleActivity(item)) {
      setScheduledItem(item);
    } else {
      openCalendarModal(item, openModal);
    }
  };

  return (
    <>
      <WidgetContainer
        size={size === "M" ? CARD_HEIGHT_TALL : CARD_HEIGHT_NORMAL}
      >
        <ActionTitle title={`${title} (${allItems.length})`} />

        <Text>
          <LocalizedDayDate date={today} />
        </Text>
        <ScrollContainer>
          <ElementsContainer>
            {allItems?.map((item) => {
              const { startDate, endDate, itemTitle, description, allDay } =
                normalizeItem(item);

              return (
                <Fragment key={item.id}>
                  <ColumnsContainer
                    key={item.id}
                    onClick={() => handleOpenModal(item)}
                  >
                    {allDay ? (
                      <T translationKey="label.all_day" />
                    ) : (
                      <LocalizedTime date={startDate} />
                    )}
                    <div>
                      <Text textStyle="Heading.XS">
                        {translateLabel(itemTitle)}
                      </Text>
                      <MeetingLength
                        allDay={allDay}
                        startDate={startDate}
                        endDate={endDate}
                      />
                      <Text textStyle="Body.S"> {description}</Text>
                    </div>
                  </ColumnsContainer>
                  <Line />
                </Fragment>
              );
            })}
          </ElementsContainer>
        </ScrollContainer>
      </WidgetContainer>
      {scheduledItem && (
        <ScheduleActivityModal
          isOpen={Boolean(scheduledItem)}
          item={scheduledItem}
          closeModal={() => setScheduledItem(undefined)}
        />
      )}
    </>
  );
};
